import { useState, useEffect } from "react";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Users, Clock, FileText, Calendar, FolderOpen } from "lucide-react";

// Import refactored components
import {
  EmployeeListTab,
  AttendanceTab,
  PayslipsTab,
  LeaveTab,
  DocumentsTab,
  EmployeeFormDialog,
  PayslipDialog,
  ViewPayslipDialog,
  LeaveDialog,
  defaultEmployee,
  defaultPayslipData,
  defaultLeaveData,
} from "@/components/employees";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Employees() {
  const [activeTab, setActiveTab] = useState("employees");
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState(defaultEmployee);
  
  // Attendance state
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const today = new Date().toISOString().split("T")[0];
  const [attendanceDate, setAttendanceDate] = useState(today);
  const [attendanceEndDate, setAttendanceEndDate] = useState(today);
  const [attendanceEmployee, setAttendanceEmployee] = useState("");
  
  // Payslip state
  const [payslipDialogOpen, setPayslipDialogOpen] = useState(false);
  const [payslipEmployee, setPayslipEmployee] = useState("");
  const [payslipData, setPayslipData] = useState(defaultPayslipData);
  const [generatedPayslip, setGeneratedPayslip] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [viewPayslipDialog, setViewPayslipDialog] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  
  // Leave state
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leaveData, setLeaveData] = useState(defaultLeaveData);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (activeTab === "attendance") {
      fetchAttendance();
    } else if (activeTab === "payslips") {
      fetchPayslips();
    } else if (activeTab === "leave") {
      fetchLeaveRequests();
    }
  }, [activeTab, attendanceDate, attendanceEndDate, attendanceEmployee]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees?include_inactive=true`);
      // Sort employees alphabetically by name
      const sortedEmployees = (response.data || []).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setEmployees(sortedEmployees);
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendance = async () => {
    try {
      let url = `${API}/employees/attendance/records?`;
      if (attendanceEmployee && attendanceEmployee !== "all") {
        url += `employee_id=${attendanceEmployee}&`;
      }
      const start = attendanceDate;
      // If no end date is set, default to the start date (single-day mode)
      const end = attendanceEndDate || attendanceDate;
      if (start) url += `start_date=${start}&`;
      if (end) url += `end_date=${end}`;
      const response = await axios.get(url);
      setAttendanceRecords(response.data);
    } catch (error) {
      console.error("Error fetching attendance:", error);
    }
  };

  const fetchPayslips = async () => {
    try {
      const response = await axios.get(`${API}/employees/payslips`);
      setPayslips(response.data);
    } catch (error) {
      console.error("Error fetching payslips:", error);
    }
  };

  const fetchLeaveRequests = async () => {
    try {
      const response = await axios.get(`${API}/employees/leave/requests`);
      setLeaveRequests(response.data);
    } catch (error) {
      console.error("Error fetching leave requests:", error);
    }
  };

  const handleSaveEmployee = async () => {
    try {
      if (selectedEmployee) {
        await axios.put(`${API}/employees/${selectedEmployee.id}`, formData);
        toast.success("Employee updated successfully");
      } else {
        await axios.post(`${API}/employees`, formData);
        toast.success("Employee created successfully");
      }
      setDialogOpen(false);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save employee");
    }
  };

  const handleDeleteEmployee = async (id) => {
    if (!window.confirm("Are you sure you want to deactivate this employee?")) return;
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success("Employee deactivated");
      fetchEmployees();
    } catch (error) {
      toast.error("Failed to deactivate employee");
    }
  };

  const handleReactivateEmployee = async (id) => {
    // Reactivates an employee that was previously soft-deleted (is_active=false).
    // Uses the same PUT /employees/{id} endpoint that already accepts is_active
    // in the EmployeeUpdate model.
    if (!window.confirm("Reactivate this employee?")) return;
    try {
      await axios.put(`${API}/employees/${id}`, { is_active: true });
      toast.success("Employee reactivated");
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate employee");
    }
  };

  const handleClockIn = async (employeeId) => {
    try {
      await axios.post(`${API}/employees/attendance/clock-in?employee_id=${employeeId}`);
      toast.success("Clocked in successfully");
      fetchAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clock in");
    }
  };

  const handleClockOut = async (employeeId) => {
    try {
      const response = await axios.post(`${API}/employees/attendance/clock-out?employee_id=${employeeId}`);
      toast.success(`Clocked out. Hours worked: ${response.data.hours_worked}`);
      fetchAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clock out");
    }
  };

  const handleGeneratePayslip = async () => {
    if (!payslipEmployee) {
      toast.error("Please select an employee");
      return;
    }
    try {
      const response = await axios.post(`${API}/employees/payslips/generate`, {
        employee_id: payslipEmployee,
        ...payslipData
      });
      setGeneratedPayslip(response.data);
      toast.success("Payslip generated successfully");
      fetchPayslips();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate payslip");
    }
  };

  const handleCreateLeaveRequest = async () => {
    try {
      await axios.post(`${API}/employees/leave/request`, leaveData);
      toast.success("Leave request created");
      setLeaveDialogOpen(false);
      fetchLeaveRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create leave request");
    }
  };

  const handleLeaveAction = async (requestId, status) => {
    try {
      await axios.put(`${API}/employees/leave/request/${requestId}?status=${status}`);
      toast.success(`Leave request ${status}`);
      fetchLeaveRequests();
    } catch (error) {
      toast.error("Failed to update leave request");
    }
  };

  const openNewEmployee = () => {
    setSelectedEmployee(null);
    setFormData(defaultEmployee);
    setDialogOpen(true);
  };

  const openEditEmployee = (employee) => {
    setSelectedEmployee(employee);
    setFormData({
      ...defaultEmployee,
      ...employee,
    });
    setDialogOpen(true);
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? `${emp.first_name} ${emp.last_name}` : "Unknown";
  };

  const activeEmployees = employees.filter(e => e.is_active);

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Users className="h-6 w-6" />
          Employee Management
        </h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-3xl">
          <TabsTrigger value="employees" className="flex items-center gap-2" data-testid="employees-tab">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Employees</span>
          </TabsTrigger>
          <TabsTrigger value="attendance" className="flex items-center gap-2" data-testid="attendance-tab">
            <Clock className="h-4 w-4" />
            <span className="hidden sm:inline">Attendance</span>
          </TabsTrigger>
          <TabsTrigger value="payslips" className="flex items-center gap-2" data-testid="payslips-tab">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Payslips</span>
          </TabsTrigger>
          <TabsTrigger value="leave" className="flex items-center gap-2" data-testid="leave-tab">
            <Calendar className="h-4 w-4" />
            <span className="hidden sm:inline">Leave</span>
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center gap-2" data-testid="documents-tab">
            <FolderOpen className="h-4 w-4" />
            <span className="hidden sm:inline">Documents</span>
          </TabsTrigger>
        </TabsList>

        {/* Employees Tab */}
        <TabsContent value="employees">
          <EmployeeListTab
            employees={employees}
            loading={loading}
            onAddEmployee={openNewEmployee}
            onEditEmployee={openEditEmployee}
            onDeleteEmployee={handleDeleteEmployee}
            onReactivateEmployee={handleReactivateEmployee}
          />
        </TabsContent>

        {/* Attendance Tab */}
        <TabsContent value="attendance">
          <AttendanceTab
            activeEmployees={activeEmployees}
            attendanceRecords={attendanceRecords}
            attendanceDate={attendanceDate}
            setAttendanceDate={setAttendanceDate}
            attendanceEndDate={attendanceEndDate}
            setAttendanceEndDate={setAttendanceEndDate}
            attendanceEmployee={attendanceEmployee}
            setAttendanceEmployee={setAttendanceEmployee}
            onClockIn={handleClockIn}
            onClockOut={handleClockOut}
            getEmployeeName={getEmployeeName}
            refreshAttendance={fetchAttendance}
          />
        </TabsContent>

        {/* Payslips Tab */}
        <TabsContent value="payslips">
          <PayslipsTab
            payslips={payslips}
            onNewPayslip={() => setPayslipDialogOpen(true)}
            onViewPayslip={(payslip) => {
              setSelectedPayslip(payslip);
              setViewPayslipDialog(true);
            }}
          />
        </TabsContent>

        {/* Leave Tab */}
        <TabsContent value="leave">
          <LeaveTab
            activeEmployees={activeEmployees}
            leaveRequests={leaveRequests}
            getEmployeeName={getEmployeeName}
            onNewLeaveRequest={() => setLeaveDialogOpen(true)}
            onLeaveAction={handleLeaveAction}
          />
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <DocumentsTab employees={employees} />
        </TabsContent>
      </Tabs>

      {/* Employee Form Dialog */}
      <EmployeeFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        selectedEmployee={selectedEmployee}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSaveEmployee}
      />

      {/* Payslip Generation Dialog */}
      <PayslipDialog
        open={payslipDialogOpen}
        onOpenChange={setPayslipDialogOpen}
        activeEmployees={activeEmployees}
        payslipEmployee={payslipEmployee}
        setPayslipEmployee={setPayslipEmployee}
        payslipData={payslipData}
        setPayslipData={setPayslipData}
        generatedPayslip={generatedPayslip}
        setGeneratedPayslip={setGeneratedPayslip}
        onGenerate={handleGeneratePayslip}
      />

      {/* View Payslip Dialog */}
      <ViewPayslipDialog
        open={viewPayslipDialog}
        onOpenChange={setViewPayslipDialog}
        payslip={selectedPayslip}
      />

      {/* Leave Request Dialog */}
      <LeaveDialog
        open={leaveDialogOpen}
        onOpenChange={setLeaveDialogOpen}
        activeEmployees={activeEmployees}
        leaveData={leaveData}
        setLeaveData={setLeaveData}
        onSubmit={handleCreateLeaveRequest}
      />
    </div>
  );
}
